"use client";

import { useNode } from "@craftjs/core";
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useResponsive } from "../../hooks/useResponsive";
import { Box, Typography, Button, CircularProgress, IconButton } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CloseIcon from "@mui/icons-material/Close";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { uploadImage } from "../../firebase/storage";
import { selectedOutlineSx, labelStyle, removeButtonStyle } from "../../config/settingsStyles";
import { SliderField, CheckboxField, SettingsSection } from "../ui/form/SettingsField";

interface GalleryImage {
  src: string;
  alt: string;
  caption?: string;
}

// Lightbox Component - uses portal to render in preview container
interface GalleryLightboxProps {
  images: GalleryImage[];
  currentIndex: number;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

const GalleryLightbox: React.FC<GalleryLightboxProps> = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Find the preview container or fall back to body
    const previewContainer = document.querySelector(".preview-container") as HTMLElement;
    setContainer(previewContainer || document.body);
    // Trigger animation after mount
    requestAnimationFrame(() => setIsVisible(true));
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  if (!container) return null;

  const lightbox = (
    <>
      {/* Backdrop */}
      <Box
        onClick={handleClose}
        sx={{
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(0, 0, 0, 0.9)",
          zIndex: 1100,
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.3s ease-in-out",
        }}
      />
      {/* Content */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1200,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? "scale(1)" : "scale(0.9)",
          transition: "opacity 0.3s ease-in-out, transform 0.3s ease-in-out",
          pointerEvents: "none",
        }}
      >
        {/* Close Button */}
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            color: "white",
            bgcolor: "rgba(0,0,0,0.5)",
            pointerEvents: "auto",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Previous Button */}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          sx={{
            position: "absolute",
            left: 16,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
            bgcolor: "rgba(0,0,0,0.5)",
            pointerEvents: "auto",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <ChevronLeftIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {/* Image */}
        <Box
          component="img"
          src={images[currentIndex].src}
          alt={images[currentIndex].alt}
          onClick={(e) => e.stopPropagation()}
          sx={{
            maxWidth: "85%",
            maxHeight: "85%",
            objectFit: "contain",
            pointerEvents: "auto",
            borderRadius: 1,
          }}
        />

        {/* Next Button */}
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          sx={{
            position: "absolute",
            right: 16,
            top: "50%",
            transform: "translateY(-50%)",
            color: "white",
            bgcolor: "rgba(0,0,0,0.5)",
            pointerEvents: "auto",
            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
          }}
        >
          <ChevronRightIcon sx={{ fontSize: 32 }} />
        </IconButton>

        {/* Image Counter */}
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            color: "white",
            bgcolor: "rgba(0,0,0,0.5)",
            px: 2,
            py: 0.5,
            borderRadius: 1,
            fontSize: "0.875rem",
            pointerEvents: "auto",
          }}
        >
          {currentIndex + 1} / {images.length}
        </Box>
      </Box>
    </>
  );

  return ReactDOM.createPortal(lightbox, container);
};

interface GalleryProps {
  images?: GalleryImage[];
  columns?: number;
  gap?: number;
  showCaptions?: boolean;
  enableLightbox?: boolean;
  maxWidth?: number;
  paddingX?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

export const Gallery = ({
  images = [
    { src: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=400", alt: "Before & After 1" },
    { src: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400", alt: "Before & After 2" },
    { src: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400", alt: "Before & After 3" },
  ],
  columns = 3,
  gap = 16,
  showCaptions = false,
  enableLightbox = true,
  maxWidth = 0,
  paddingX = 16,
  paddingTop = 16,
  paddingBottom = 16,
}: GalleryProps) => {
  const {
    connectors: { connect, drag },
    selected,
  } = useNode((state) => ({
    selected: state.events.selected,
  }));

  const containerRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useResponsive(containerRef as React.RefObject<HTMLElement>);

  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  return (
    <Box
      ref={(ref: HTMLDivElement | null) => {
        if (ref) {
          connect(drag(ref));
          (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = ref;
        }
      }}
      sx={{
        ...(maxWidth > 0 && { maxWidth: `${maxWidth}px`, mx: "auto" }),
        px: `${paddingX}px`,
        pt: `${paddingTop}px`,
        pb: `${paddingBottom}px`,
        ...(selected && selectedOutlineSx),
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isMobile ? (columns === 1 ? "1fr" : "repeat(2, 1fr)") : `repeat(${columns}, 1fr)`,
          gap: isMobile ? `${Math.max(8, gap * 0.5)}px` : `${gap}px`,
        }}
      >
        {images.map((image, index) => (
          <Box
            key={index}
            onClick={() => enableLightbox && setLightboxIndex(index)}
            sx={{
              position: "relative",
              aspectRatio: "1",
              overflow: "hidden",
              borderRadius: 2,
              cursor: enableLightbox ? "pointer" : "default",
              "&:hover img": {
                transform: "scale(1.05)",
              },
            }}
          >
            <Box
              component="img"
              src={image.src}
              alt={image.alt}
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transition: "transform 0.3s ease",
              }}
            />
            {showCaptions && image.caption && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: "rgba(0, 0, 0, 0.7)",
                  color: "white",
                  p: 1,
                  fontSize: "0.875rem",
                }}
              >
                {image.caption}
              </Box>
            )}
          </Box>
        ))}
      </Box>

      {/* Lightbox - rendered via portal to preview container */}
      {lightboxIndex !== null && (
        <GalleryLightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onPrev={() => setLightboxIndex((prev) => (prev! > 0 ? prev! - 1 : images.length - 1))}
          onNext={() => setLightboxIndex((prev) => (prev! < images.length - 1 ? prev! + 1 : 0))}
        />
      )}
    </Box>
  );
};

const GallerySettings = () => {
  const {
    actions: { setProp },
    images,
    columns,
    gap,
    showCaptions,
    enableLightbox,
    maxWidth,
    paddingX,
    paddingTop,
    paddingBottom,
  } = useNode((node) => ({
    images: node.data.props.images,
    columns: node.data.props.columns,
    gap: node.data.props.gap,
    showCaptions: node.data.props.showCaptions,
    enableLightbox: node.data.props.enableLightbox,
    maxWidth: node.data.props.maxWidth,
    paddingX: node.data.props.paddingX,
    paddingTop: node.data.props.paddingTop,
    paddingBottom: node.data.props.paddingBottom,
  }));

  const [newImageUrl, setNewImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

  const addImage = () => {
    if (newImageUrl.trim()) {
      setProp((props: GalleryProps) => {
        props.images = [...(props.images || []), { src: newImageUrl.trim(), alt: "Gallery image" }];
      });
      setNewImageUrl("");
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploading(true);
    setUploadError(null);

    for (const file of Array.from(files)) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        setUploadError("Invalid file type. Allowed: JPG, PNG, GIF, WebP, SVG");
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        setUploadError("File too large. Maximum size is 5MB");
        continue;
      }

      try {
        const url = await uploadImage(file);
        setProp((props: GalleryProps) => {
          props.images = [...(props.images || []), { src: url, alt: file.name }];
        });
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : "Upload failed");
      }
    }

    setIsUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeImage = (index: number) => {
    setProp((props: GalleryProps) => {
      props.images = props.images?.filter((_, i) => i !== index);
    });
  };

  return (
    <SettingsSection>
      <SliderField
        label="Columns"
        value={columns || 3}
        onChange={(v) => setProp((props: GalleryProps) => (props.columns = v))}
        min={1}
        max={6}
      />

      <SliderField
        label="Gap"
        value={gap || 16}
        onChange={(v) => setProp((props: GalleryProps) => (props.gap = v))}
        min={0}
        max={32}
        unit="px"
      />

      <CheckboxField
        label="Show Captions"
        checked={showCaptions || false}
        onChange={(v) => setProp((props: GalleryProps) => (props.showCaptions = v))}
      />

      <CheckboxField
        label="Enable Lightbox"
        checked={enableLightbox !== false}
        onChange={(v) => setProp((props: GalleryProps) => (props.enableLightbox = v))}
      />

      <SliderField
        label="Max Width"
        value={maxWidth || 0}
        onChange={(v) => setProp((props: GalleryProps) => (props.maxWidth = v))}
        min={0}
        max={1600}
        unit="px"
      />

      <SliderField
        label="Padding X"
        value={paddingX ?? 16}
        onChange={(v) => setProp((props: GalleryProps) => (props.paddingX = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Top"
        value={paddingTop ?? 16}
        onChange={(v) => setProp((props: GalleryProps) => (props.paddingTop = v))}
        min={0}
        max={120}
        unit="px"
      />

      <SliderField
        label="Padding Bottom"
        value={paddingBottom ?? 16}
        onChange={(v) => setProp((props: GalleryProps) => (props.paddingBottom = v))}
        min={0}
        max={120}
        unit="px"
      />

      <div>
        <label style={labelStyle}>Images</label>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, maxHeight: 192, overflowY: "auto" }}>
          {images?.map((img: GalleryImage, index: number) => (
            <Box
              key={index}
              sx={{ display: "flex", alignItems: "center", gap: 1, bgcolor: "grey.100", p: 1, borderRadius: 1 }}
            >
              <Box
                component="img"
                src={img.src}
                alt={img.alt}
                sx={{ width: 40, height: 40, objectFit: "cover", borderRadius: 1 }}
              />
              <Typography
                variant="caption"
                sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {img.src}
              </Typography>
              <button onClick={() => removeImage(index)} style={removeButtonStyle}>
                ✕
              </button>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
          <input
            type="text"
            placeholder="Image URL"
            value={newImageUrl}
            onChange={(e) => setNewImageUrl(e.target.value)}
            style={{ flex: 1, padding: "8px 12px", border: "1px solid #ddd", borderRadius: 4, fontSize: 13 }}
          />
          <Button onClick={addImage} variant="contained" size="small" sx={{ textTransform: "none" }}>
            Add
          </Button>
        </Box>

        <Box sx={{ mt: 1 }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          <Button
            fullWidth
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            variant="outlined"
            startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUploadIcon />}
            sx={{
              textTransform: "none",
              bgcolor: "grey.100",
              borderColor: "grey.300",
              color: "text.primary",
              "&:hover": { bgcolor: "grey.200", borderColor: "grey.400" },
            }}
          >
            {isUploading ? "Uploading..." : "Upload Images"}
          </Button>
          {uploadError && (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              {uploadError}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
            Max 5MB per image. Supports JPG, PNG, GIF, WebP, SVG
          </Typography>
        </Box>
      </div>
    </SettingsSection>
  );
};

Gallery.craft = {
  displayName: "Gallery",
  props: {
    images: [
      { src: "https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=400", alt: "Before & After 1" },
      { src: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400", alt: "Before & After 2" },
      { src: "https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400", alt: "Before & After 3" },
    ],
    columns: 3,
    gap: 16,
    showCaptions: false,
    enableLightbox: true,
    maxWidth: 0,
    paddingX: 16,
    paddingTop: 16,
    paddingBottom: 16,
  },
  related: {
    settings: GallerySettings,
  },
};
